import axios from 'axios';
import { Campaign } from '../Campaign.types';
import { CampaignStatus } from '@/api/gql/graphql';
import {
  useGetCampaignDailyStats,
  useGetCampaignStats,
  useUpdateCampaignStatus,
} from '@/api/hooks/b2bFinance/useCampaign';
import { useFormatDate } from '@/hooks/useFormatDate';
import { useAudio } from './useAudio';
import { useCampaignStore } from '@/store/CampaignStore/CampaignStore';
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/useToast';

export const useCampaignDetail = (campaign: Campaign) => {
  const { isOpenCampaignDetailModal, openCampaignDetailModal } =
    useCampaignStore();
  const { audioRef, isPlaying, togglePlay } = useAudio(
    campaign.campaignResource,
  );
  const { formatDate, getDurationInDays } = useFormatDate();
  const { mutate: updateCampaignStatus } = useUpdateCampaignStatus();
  const { data: campaignStats } = useGetCampaignStats(campaign?._id);
  const { data: campaignDailyStats } = useGetCampaignDailyStats(campaign?._id);

  const [downloading, setDownloading] = useState(false);
  const { showToast } = useToast();
  const downloadFile = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('authToken');

      const response = await axios({
        url: `${process.env.REACT_APP_FILE_UPLOAD_URL}/campaigns/${campaign._id}/download-stats`,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'stats-campagne.pdf';

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (error) {
      setDownloading(false);
      showToast('Erreur lors du téléchargement', 'error', {
        autoClose: 5000,
        position: 'top-right',
      });
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const getConversionRate = useCallback(
    (views: number, clicks: number): number => {
      if (views === 0) return 0;
      return Number(((clicks / views) * 100).toFixed(1));
    },
    [],
  );

  const handleUpdateStatus = (status: CampaignStatus) => {
    updateCampaignStatus({
      id: campaign._id,
      input: { campaignStatus: status },
    });
  };

  const getButtonTitle = (status: string) => {
    switch (status.toUpperCase()) {
      case CampaignStatus.Pending:
        return 'Lancer la campagne';
      case CampaignStatus.Completed:
        return 'Relancer la campagne';
      default:
        return 'Lancer la campagne';
    }
  };

  return {
    isOpenCampaignDetailModal,
    openCampaignDetailModal,
    audioRef,
    isPlaying,
    togglePlay,
    formatDate,
    getDurationInDays,
    downloadFile,
    getConversionRate,
    handleUpdateStatus,
    downloading,
    getButtonTitle,
    campaignStats,
    campaignDailyStats,
  };
};
